/**
 * The browser-chrome colour — ONE source.
 *
 * The same two colours are written in five places that cannot import each
 * other: the `theme-color` metas and the first-frame <style> in index.html,
 * manifest.json's theme/background colours, offline.html's literal palette,
 * and this module. They drifted: the manifest kept the old cream (#f6f3ef)
 * after index.html moved to the cool palette, so an installed app's splash
 * screen and task-switcher card were a different colour from the app. The
 * values here ARE the `--background` tokens in index.css, converted to hex,
 * and tests/themeColor.test.ts converts the tokens and holds every one of the
 * five to them.
 */
export const THEME_COLOR = {
  light: "#f0eef6", // --background: 252 30% 95%
  dark: "#16141c", // --background (dark): 252 16% 9.5%
} as const;

/**
 * Keep the browser's bars on the colour of the theme the reader CHOSE.
 *
 * The metas in index.html pick light or dark by `prefers-color-scheme` — the
 * DEVICE's setting. Someone who picked Dark in SaleFish on a phone set to
 * Light got a pale Safari toolbar over a dark app (and the reverse). The app's
 * choice is the `.light` / `.dark` class on <html> (theme-init.js before first
 * paint, ThemeProvider after), so every theme-color meta follows that class.
 * With neither class present the metas are left to their media queries.
 *
 * Returns a disposer (for the test; the app keeps it for its lifetime).
 */
export function installThemeColorSync(doc: Document = document): () => void {
  const apply = () => {
    const root = doc.documentElement;
    const dark = root.classList.contains("dark");
    const light = root.classList.contains("light");
    if (dark === light) return; // neither (or both, mid-swap): leave the media queries in charge
    const color = dark ? THEME_COLOR.dark : THEME_COLOR.light;
    doc.querySelectorAll('meta[name="theme-color"]').forEach((meta) => {
      if (meta.getAttribute("content") !== color) meta.setAttribute("content", color);
    });
  };
  apply();
  if (typeof MutationObserver === "undefined") return () => {};
  const observer = new MutationObserver(apply);
  observer.observe(doc.documentElement, { attributes: true, attributeFilter: ["class"] });
  return () => observer.disconnect();
}
